import {
    getLast6Months,
    getLastMonth,
    getLastWeek,
    getThisMonth,
    getThisWeek,
    getThisYear,
} from './dateScripts'
import { localDateOnlyString } from './dateParsing'
import {
    isCurrencyRangeFilterValue,
    isMeaningfulWorkQFilterValue,
    normalizeCurrencyRange,
    normalizeIncidentDateFilterValue,
} from './filters'
import {
    getLienStageErrorMessages,
    pickLienCreationErrors,
} from './lienCreationErrors'

const PRACTICE_SCOPED_FILTER_KEYS = [
    'practice',
    'location',
    'practice_treatment_status',
    'balances',
    'balance',
    'practice_initial_treatment_date',
    'lien_created_date',
    'lien_notice_date',
    'lien_original_date',
    'lien_amended_date',
    'lien_satisfied_date',
    'lien_status',
    'lien_stage',
    'mrq_status',
    'mrq_entry_date',
]

const LIEN_FILTER_DATES_BY_FILTER = {
    lien_notice_date: ['notice_patient_dates', 'notice_practice_dates'],
    lien_original_date: ['lien_patient_dates', 'lien_practice_dates'],
    lien_amended_date: ['amended_patient_dates', 'amended_practice_dates'],
    lien_satisfied_date: ['satisfaction_patient_dates', 'satisfaction_practice_dates'],
}

const LIEN_SIDE_DISPLAY_DATE_FIELDS = [
    'notice_patient_dates',
    'notice_practice_dates',
    'lien_patient_dates',
    'lien_practice_dates',
    'amended_patient_dates',
    'amended_practice_dates',
    'satisfaction_patient_dates',
    'satisfaction_practice_dates',
]

const LIEN_CREATED_DATE_FIELDS = [
    ...LIEN_SIDE_DISPLAY_DATE_FIELDS,
    // Fallback for older payloads.
    'notice_filter_dates',
    'lien_filter_dates',
    'amended_filter_dates',
    'satisfaction_filter_dates',
    'notice_created_date',
    'lien_created_date',
    'amended_created_date',
    'satisfaction_created_date',
    'notice_date',
    'lien_date',
    'amended_date',
    'satisfaction_date',
]

const LIEN_DATE_FIELD_BY_FILTER = {
    lien_notice_date: 'notice_created_date',
    lien_original_date: 'lien_created_date',
    lien_amended_date: 'amended_created_date',
    lien_satisfied_date: 'satisfaction_created_date',
}

const LIEN_DATE_DISPLAY_FALLBACK_BY_FILTER = {
    lien_notice_date: 'notice_date',
    lien_original_date: 'lien_date',
    lien_amended_date: 'amended_date',
    lien_satisfied_date: 'satisfaction_date',
}

const LIEN_FILTER_DATES_FALLBACK_BY_FILTER = {
    lien_notice_date: 'notice_filter_dates',
    lien_original_date: 'lien_filter_dates',
    lien_amended_date: 'amended_filter_dates',
    lien_satisfied_date: 'satisfaction_filter_dates',
}

function rowHasNoticeCreationErrors(mergedRow) {
    return getLienStageErrorMessages(pickLienCreationErrors(mergedRow), 'intent').length > 0
}

function collectLienFilterCandidateDates(mergedRow, filterKey) {
    const sideFields = LIEN_FILTER_DATES_BY_FILTER[filterKey] || []
    const fromSides = []
    for (const field of sideFields) {
        const value = mergedRow?.[field]
        if (Array.isArray(value)) {
            fromSides.push(...value.filter(Boolean))
        } else if (value) {
            fromSides.push(value)
        }
    }
    if (fromSides.length > 0) {
        return fromSides
    }

    const arrayField = LIEN_FILTER_DATES_FALLBACK_BY_FILTER[filterKey]
    const fromArray = arrayField && Array.isArray(mergedRow?.[arrayField])
        ? mergedRow[arrayField].filter(Boolean)
        : []
    if (fromArray.length > 0) {
        return fromArray
    }

    const createdField = LIEN_DATE_FIELD_BY_FILTER[filterKey]
    const displayField = LIEN_DATE_DISPLAY_FALLBACK_BY_FILTER[filterKey]
    const fallbacks = []
    if (createdField && mergedRow?.[createdField]) {
        fallbacks.push(mergedRow[createdField])
    }
    if (displayField && mergedRow?.[displayField]) {
        fallbacks.push(mergedRow[displayField])
    }
    return fallbacks
}

function anyDateMatchesRange(dateValues, range) {
    if (!range) {
        return true
    }
    if (!Array.isArray(dateValues) || dateValues.length === 0) {
        return false
    }
    return dateValues.some((value) => matchesDateRange(value, range))
}

function toIdList(value) {
    if (value == null || value === '') {
        return []
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (item == null) return null
                if (typeof item === 'object') {
                    return item.value ?? item.id ?? null
                }
                return item
            })
            .filter((id) => id !== null && id !== undefined && id !== '')
            .map((id) => String(id))
    }
    if (typeof value === 'object' && ('value' in value || 'id' in value)) {
        const id = value.value ?? value.id
        return id == null || id === '' ? [] : [String(id)]
    }
    return [String(value)]
}

function addDaysLocal(base, days) {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate())
    d.setDate(d.getDate() + Number(days))
    return d
}

function datePresetToPipe(cfg) {
    if (!cfg || typeof cfg !== 'object') {
        return null
    }
    const type = cfg.type
    if (!type) {
        return null
    }
    switch (type) {
        case 'today': {
            const today = localDateOnlyString(new Date())
            return `${today}|${today}`
        }
        case 'tomorrow': {
            const t = addDaysLocal(new Date(), 1)
            const tomorrow = localDateOnlyString(t)
            return `${tomorrow}|${tomorrow}`
        }
        case 'this_week':
            return getThisWeek(new Date())
        case 'last_week':
            return getLastWeek(new Date())
        case 'this_month':
            return getThisMonth(new Date())
        case 'last_month':
            return getLastMonth()
        case 'last_6_month':
            return getLast6Months()
        case 'this_year':
            return getThisYear(new Date())
        case 'last_year': {
            const y = new Date().getFullYear() - 1
            return `${y}-01-01|${y}-12-31`
        }
        case 'range': {
            const fromDay = cfg.from_day ?? cfg.from
            const toDay = cfg.to_day ?? cfg.to
            if (fromDay === null || fromDay === undefined || fromDay === ''
                || toDay === null || toDay === undefined || toDay === '') {
                return null
            }
            const start = localDateOnlyString(addDaysLocal(new Date(), Number(fromDay)))
            const end = localDateOnlyString(addDaysLocal(new Date(), Number(toDay)))
            return `${start}|${end}`
        }
        case 'ge':
        case 'le':
            if (!cfg.date) {
                return null
            }
            return { op: type, date: cfg.date }
        default:
            return null
    }
}

/**
 * Merge Redux mega-filters with Enforce presets from workq.filter_conditions.
 * Request / Redux values win over Enforce when both are present.
 */
export function resolvePracticeDisplayFilters(filters = {}, filterConditions = null) {
    const resolved = { ...(filters || {}) }

    if (!filterConditions || typeof filterConditions !== 'object') {
        return resolved
    }

    for (const key of PRACTICE_SCOPED_FILTER_KEYS) {
        const typeKey = `${key}_filter_type`
        const filterType = filterConditions[typeKey]
        if (Number(filterType) !== 1) {
            continue
        }
        if (isMeaningfulWorkQFilterValue(resolved[key])) {
            continue
        }

        const cfg = filterConditions[key]
        if (key === 'balances' || key === 'balance') {
            continue
        }
        if (key.includes('_date') || key === 'mrq_entry_date') {
            const pipeOrOp = datePresetToPipe(cfg)
            if (pipeOrOp) {
                resolved[key] = pipeOrOp
            }
            continue
        }
        if (isMeaningfulWorkQFilterValue(cfg)) {
            resolved[key] = cfg
        }
    }

    return resolved
}

function parseDateRange(value) {
    if (!isMeaningfulWorkQFilterValue(value)) {
        return null
    }
    if (typeof value === 'string') {
        if (value.includes('|')) {
            const [start, end] = value.split('|')
            return {
                start: start || null,
                end: end || null,
            }
        }
        return { start: value, end: value }
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
        if (value.op || value.date) {
            return null
        }
        const start = value.start ?? value.start_date ?? null
        const end = value.end ?? value.end_date ?? null
        if (!start && !end) {
            return null
        }
        return { start, end }
    }
    return null
}

function dateOnly(value) {
    if (!value) {
        return null
    }
    const s = String(value)
    return s.length >= 10 ? s.slice(0, 10) : s
}

function matchesDateRange(rawDate, range) {
    if (!range) {
        return true
    }
    const d = dateOnly(rawDate)
    if (!d) {
        return false
    }
    if (range.start && d < dateOnly(range.start)) {
        return false
    }
    if (range.end && d > dateOnly(range.end)) {
        return false
    }
    return true
}

function matchesDateOperator(rawDate, filterValue) {
    const normalized = normalizeIncidentDateFilterValue(filterValue)
    if (!normalized) {
        return true
    }
    if (typeof normalized === 'string') {
        return matchesDateRange(rawDate, parseDateRange(normalized))
    }
    if (typeof normalized === 'object') {
        const d = dateOnly(rawDate)
        if (!d) {
            return false
        }
        const op = String(normalized.op || '').toLowerCase()
        const bound = dateOnly(normalized.date)
        if (!bound) {
            return true
        }
        if (op === 'ge') {
            return d >= bound
        }
        if (op === 'le') {
            return d <= bound
        }
        if (normalized.start || normalized.end) {
            return matchesDateRange(rawDate, {
                start: normalized.start,
                end: normalized.end,
            })
        }
    }
    return true
}

function matchesCurrencyRange(amount, rangeValue) {
    if (!isCurrencyRangeFilterValue(rangeValue)) {
        return true
    }
    const range = normalizeCurrencyRange(rangeValue)
    if (!range) {
        return true
    }
    const n = Number(amount)
    if (!Number.isFinite(n)) {
        return false
    }
    if (range.min != null && n < range.min) {
        return false
    }
    if (range.max != null && n > range.max) {
        return false
    }
    return true
}

function practiceBalance(row) {
    if (row?.total_balances != null && row.total_balances !== '') {
        return row.total_balances
    }
    if (row?.current_balance != null && row.current_balance !== '') {
        return row.current_balance
    }
    return row?.balance
}

function indexRowsByPracticeId(rows) {
    const map = new Map()
    ;(rows || []).forEach((row) => {
        if (!row || row.practice_id == null) {
            return
        }
        const id = String(row.practice_id)
        const existing = map.get(id) || {}
        map.set(id, { ...existing, ...row })
    })
    return map
}

function practiceMatchesFilters(mergedRow, filters) {
    const practiceIds = toIdList(filters.practice)
    if (practiceIds.length && !practiceIds.includes(String(mergedRow.practice_id))) {
        return false
    }

    const locationIds = toIdList(filters.location)
    if (locationIds.length && !locationIds.includes(String(mergedRow.location_id))) {
        return false
    }

    const statusIds = toIdList(filters.practice_treatment_status)
    if (statusIds.length) {
        const status = mergedRow.status ?? mergedRow.status_id
        if (status == null || !statusIds.includes(String(status))) {
            return false
        }
    }

    const balanceFilter = filters.balances ?? filters.balance
    if (isCurrencyRangeFilterValue(balanceFilter) && !matchesCurrencyRange(practiceBalance(mergedRow), balanceFilter)) {
        return false
    }

    if (isMeaningfulWorkQFilterValue(filters.practice_initial_treatment_date)) {
        const range = parseDateRange(filters.practice_initial_treatment_date)
        if (range && !matchesDateRange(mergedRow.initial_treatment, range)) {
            return false
        }
    }

    if (isMeaningfulWorkQFilterValue(filters.lien_created_date)) {
        const range = parseDateRange(filters.lien_created_date)
        if (range) {
            const candidates = []
            for (const field of LIEN_CREATED_DATE_FIELDS) {
                const value = mergedRow[field]
                if (Array.isArray(value)) {
                    candidates.push(...value.filter(Boolean))
                } else if (value) {
                    candidates.push(value)
                }
            }
            if (!anyDateMatchesRange(candidates, range)) {
                return false
            }
        }
    }

    for (const filterKey of Object.keys(LIEN_FILTER_DATES_BY_FILTER)) {
        if (!isMeaningfulWorkQFilterValue(filters[filterKey])) {
            continue
        }
        const range = parseDateRange(filters[filterKey])
        if (!range) {
            continue
        }
        if (filterKey === 'lien_notice_date' && rowHasNoticeCreationErrors(mergedRow)) {
            continue
        }
        if (!anyDateMatchesRange(collectLienFilterCandidateDates(mergedRow, filterKey), range)) {
            return false
        }
    }

    const lienStatuses = toIdList(filters.lien_status)
    if (lienStatuses.length) {
        const status = mergedRow.lien_status ?? mergedRow.liens_status
        if (status == null || status === '' || !lienStatuses.includes(String(status))) {
            return false
        }
    }

    // lien_stage is evaluated server-side (package uuid/book/page not on display rows).
    // Keep the key practice-scoped so Enforce presets merge; do not hide lines here.

    const mrqStatusIds = toIdList(filters.mrq_status)
    if (mrqStatusIds.length) {
        const mrqStatus = mergedRow.mrq_status_id
        if (mrqStatus == null || !mrqStatusIds.includes(String(mrqStatus))) {
            return false
        }
    }

    if (isMeaningfulWorkQFilterValue(filters.mrq_entry_date)) {
        const entry = mergedRow.mrq_entry_date ?? mergedRow.mrq_start_date
        const raw = filters.mrq_entry_date
        if (raw && typeof raw === 'object' && !Array.isArray(raw) && (raw.op || raw.date || raw.start || raw.end)) {
            if (!matchesDateOperator(entry, raw)) {
                return false
            }
        } else {
            const range = parseDateRange(raw)
            if (range && !matchesDateRange(entry, range)) {
                return false
            }
        }
    }

    return true
}

function hasActivePracticeScopedFilter(filters) {
    if (!filters) {
        return false
    }
    return PRACTICE_SCOPED_FILTER_KEYS.some((key) => {
        const value = filters[key]
        if (key === 'balances' || key === 'balance') {
            return isCurrencyRangeFilterValue(value) && normalizeCurrencyRange(value) != null
        }
        return isMeaningfulWorkQFilterValue(value)
    })
}

/**
 * Filter practices / liens arrays for WorkQ multi-row cells.
 * Case-level totals must keep using unfiltered API values.
 *
 * @returns {{ practices: array, liens: array }}
 */
export function filterWorkQPracticeRowsForDisplay(practices, liens, filters = {}, filterConditions = null) {
    const resolved = resolvePracticeDisplayFilters(filters, filterConditions)
    const practiceRows = Array.isArray(practices) ? practices : []
    const lienRows = Array.isArray(liens) ? liens : []

    if (!hasActivePracticeScopedFilter(resolved)) {
        return { practices: practiceRows, liens: lienRows }
    }

    const byId = indexRowsByPracticeId([...practiceRows, ...lienRows])
    const allowed = new Set()

    byId.forEach((merged, practiceId) => {
        if (practiceMatchesFilters(merged, resolved)) {
            allowed.add(practiceId)
        }
    })

    return {
        practices: practiceRows.filter((row) => row?.practice_id != null && allowed.has(String(row.practice_id))),
        liens: lienRows.filter((row) => row?.practice_id != null && allowed.has(String(row.practice_id))),
    }
}
